import React, { useState } from 'react';
import { createPurchaseBatch } from '../inventoryApi';
import {
  Modal, FieldLabel, TextInput, SelectInput, TextArea,
  PrimaryButton, SecondaryButton, Spinner, COLORS, SEMANTIC,
} from './InventoryUIKit';

/**
 * PurchaseBatchModal
 *
 * "Add Purchase Batch" form modal. Creates a new FIFO purchase batch via
 * POST /inventory/purchase-batches/.
 *
 * @param {Array} inventoryItems - [{id, item_name, ...}] for the dropdown
 * @param {() => void} onClose
 * @param {(batch:Object) => void} onSuccess - called with the created batch
 */
const EMPTY_FORM = {
  inventory_item: '',
  purchase_date: new Date().toISOString().slice(0, 10),
  purchase_quantity: '',
  unit_price: '',
  supplier: '',
  invoice_number: '',
  remarks: '',
};

const PurchaseBatchModal = ({ inventoryItems = [], onClose, onSuccess }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  };

  const validate = () => {
    const next = {};
    if (!form.inventory_item) next.inventory_item = 'Please select an inventory item.';
    if (!form.purchase_date) next.purchase_date = 'Purchase date is required.';
    const qty = Number(form.purchase_quantity);
    if (!form.purchase_quantity || qty <= 0) next.purchase_quantity = 'Enter a quantity greater than 0.';
    const price = Number(form.unit_price);
    if (form.unit_price === '' || price < 0) next.unit_price = 'Enter a valid unit price.';
    if (!form.supplier.trim()) next.supplier = 'Supplier is required.';
    if (!form.invoice_number.trim()) next.invoice_number = 'Invoice number is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        inventory_item: Number(form.inventory_item),
        purchase_date: form.purchase_date,
        purchase_quantity: Number(form.purchase_quantity),
        unit_price: Number(form.unit_price),
        supplier: form.supplier.trim(),
        invoice_number: form.invoice_number.trim(),
        remarks: form.remarks.trim(),
      };
      const created = await createPurchaseBatch(payload);
      onSuccess(created);
    } catch (err) {
      setApiError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to create purchase batch. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const errorText = (field) =>
    errors[field] ? <span style={{ fontSize: '11px', color: SEMANTIC.red, marginTop: '4px', display: 'block' }}>{errors[field]}</span> : null;

  return (
    <Modal
      title="Add Purchase Batch"
      subtitle="Record a new procurement batch for FIFO costing"
      onClose={onClose}
      width="620px"
      footer={(
        <>
          <SecondaryButton onClick={onClose} disabled={submitting}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSubmit} disabled={submitting} type="submit">
            {submitting && <Spinner size={13} color={COLORS.white} borderColor="rgba(255,255,255,0.4)" />}
            {submitting ? 'Saving…' : 'Submit'}
          </PrimaryButton>
        </>
      )}
    >
      <form onSubmit={handleSubmit}>
        {apiError && (
          <div style={{ backgroundColor: '#fee2e2', border: '1px solid #fecaca', color: SEMANTIC.red, borderRadius: '6px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' }}>
            {apiError}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <FieldLabel required>Inventory Item</FieldLabel>
            <SelectInput value={form.inventory_item} onChange={(e) => update('inventory_item', e.target.value)}>
              <option value="">Select item…</option>
              {inventoryItems.map((item) => (
                <option key={item.id} value={item.id}>{item.item_name}</option>
              ))}
            </SelectInput>
            {errorText('inventory_item')}
          </div>

          <div>
            <FieldLabel required>Purchase Date</FieldLabel>
            <TextInput type="date" value={form.purchase_date} onChange={(e) => update('purchase_date', e.target.value)} />
            {errorText('purchase_date')}
          </div>

          <div>
            <FieldLabel required>Purchase Quantity</FieldLabel>
            <TextInput type="number" min="1" placeholder="e.g. 100" value={form.purchase_quantity} onChange={(e) => update('purchase_quantity', e.target.value)} />
            {errorText('purchase_quantity')}
          </div>

          <div>
            <FieldLabel required>Unit Price</FieldLabel>
            <TextInput type="number" min="0" step="0.01" placeholder="e.g. 12.50" value={form.unit_price} onChange={(e) => update('unit_price', e.target.value)} />
            {errorText('unit_price')}
          </div>

          <div>
            <FieldLabel required>Supplier</FieldLabel>
            <TextInput type="text" placeholder="e.g. MedSupply Co." value={form.supplier} onChange={(e) => update('supplier', e.target.value)} />
            {errorText('supplier')}
          </div>

          <div>
            <FieldLabel required>Invoice Number</FieldLabel>
            <TextInput type="text" placeholder="e.g. INV-2026-0451" value={form.invoice_number} onChange={(e) => update('invoice_number', e.target.value)} />
            {errorText('invoice_number')}
          </div>
        </div>

        <div>
          <FieldLabel>Remarks</FieldLabel>
          <TextArea placeholder="Optional notes about this purchase…" value={form.remarks} onChange={(e) => update('remarks', e.target.value)} />
        </div>
      </form>
    </Modal>
  );
};

export default PurchaseBatchModal;
